import {UserRole} from '@shared/enums/UserRole';
import {Insight} from '@shared/models/insight';
import { DeployType } from '@shared/common/types/main';

export type Permission =
	| 'User'
	| 'Organisational_Administrator'
	| 'Administrator'
	| 'Super_Admin';

export interface UserModel {
	is_guest_user?: boolean;
	id: string;
	surname: null | string;
	display_name: string;
	phone: string;
	state: string | null;
	first_name: string;
	email: string;
	xero_id: string;
	is_active: boolean;
	organisations: OrganisationUserRole[];
	insights: Insight[];
	user_monthly_quota: number;
	has_private_account: boolean;
	notes: string | null;
	roles: UserRole[];
	customer_role: string[];
	admin_role: string | null;
	is_multi_organisation_user?: boolean;
}

export interface InternalRole {
	id: string | null;
	name: string;
	type: InternalRoleType;
}

export interface AccessPermission {
	id: string;
	name: string;
	description: string;
	parent_id: string | null;
	type: InternalRoleType;
}

export interface UpdatePermissionAccess {
	role_id: string;
	permissions: string[];
}

export interface InternalRolesAccess {
	id: string;
	name: string;
	type: InternalRoleType;
	permissions: string[];
}

export enum InternalRoleType {
	ClientPortal = 'CustomerPortal',
	Chat = 'Chat',
}

export interface GetUsersResponse {
	current_page: number;
	page_size: number;
	results: UserModel[];
	total_items: number;
	total_pages: number;
}

export interface AddUserRequest {
	first_name: string;
	surname: string;
	display_name: string;
	user_email: string;
	user_phone: string;
	user_password: string | null;
	notes: string | null;
	force_change_password: boolean;
	role_id: string;
	organisations: OrganisationUserRole[];
	insights: string[];
}

export interface AddUserFormStep1Values {
	first_name: string;
	surname: string;
	display_name: string;
	user_email: string;
	user_phone: string;
	user_password?: string;
	notes: string | null;
	force_change_password: boolean;
	role_id: string;
	insights: string[];
	user_id: string;
}

export interface AddUserFormStep2Values {
	organisations: OrganisationUserRole[];
}
export interface OrganisationUserRole {
	deploy_type?: DeployType | null;
	id: string;
	name: string;
	admin_role: string | null;
	customer_role: string[];
	assigned_insight_ids?: string[];
	insights?: Array<string | { id: string; name: string; display_name?: string | null }>;
}

export interface CustomerOrganisationDiscoveryResponse {
	organisations: Pick<OrganisationUserRole, 'id' | 'name'>[];
}

export interface CustomerCurrentUserQuery {
	organisationId: string;
}

export interface AddUserResponseObject extends UserModel {
	'@odata.context': string;
}

export enum CreateUserOutcome {
	EXISTING_NO_CHANGES = 'existing_no_changes',
	CREATED = 'created',
}

export type AddUserResponse = {
	id?: string;
	outcome: CreateUserOutcome;
	upserted: boolean;
};

export interface CustomerUsersQuery {
	organisationId: string;
	pageSize: number;
	pageNumber: number;
	name?: string;
	email?: string;
	ssoUsers?: boolean;
	sortBy?: CustomerUserSortField;
	sortOrder?: 'asc' | 'desc';
}

export type CustomerUserSortField = 'display_name' | 'email' | 'sso' | 'organisations';

export interface CustomerUserDetailQuery {
	userId: string;
	organisationId: string;
}

export interface CustomerEmailCheckQuery {
	email: string;
	organisationId: string;
}

export enum CustomerEmailAction {
	Create = 'create',
	ContactCeade = 'contact_ceade',
}

export interface CustomerEmailCheckResponse {
	action: CustomerEmailAction;
}

export interface CustomerUserOrganisationRequest {
	id: string;
	// Omitted roles are unassigned on create and unchanged on edit.
	admin_role?: string | null;
	customer_role?: string[];
	insights: string[];
}

export interface CustomerCreateUserRequest {
	first_name: string;
	surname: string;
	display_name: string;
	user_email: string;
	user_phone: string;
	user_password: string | null;
	notes: string | null;
	force_change_password: boolean;
	role_id: string;
	organisations: CustomerUserOrganisationRequest[];
}

export interface CustomerUpdateUserRequest extends Omit<CustomerCreateUserRequest, 'force_change_password' | 'surname'> {
	surname: string | null;
	user_id: string;
}

export interface CustomerDeleteUserRequest {
	userId: string;
	organisationId: string;
}

export enum CustomerDeleteOutcome {
	AccountDeleted = 'account_deleted',
}

export interface CustomerDeleteUserResponse {
	outcome: CustomerDeleteOutcome;
	organisation_id: string;
	account_active: boolean;
}

export interface UpdateUserRequest extends AddUserRequest {
	is_guest_user?: boolean;
	user_id: string;
}

// Users roles

export interface AddUserRolesRequest {
	appRoleIds: string[];
}

// REDUX Models

export interface UsersPageModel {
	users: UserModel[] | null;
	next_skip_token?: string;
}

export interface UsersPerOrganisations {
	organisation_ids: string[];
	is_select_all: boolean;
	page_size: number;
	page_number: number;
	only_private: boolean;
}

export interface UserModelPerOrganisation {
	id: string;
	surname: string;
	display_name: string;
	phone: null | string;
	state: null | string;
	first_name: string;
	email: string;
	is_active: boolean;
	organisations: string[];
}

export interface UsersPerOrganisationsResponse {
	results: UserModelPerOrganisation[];
	total_items: number;
	total_pages: number;
	current_page: number;
	page_size: number;
}
